import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { 
  Trophy, Zap, Flame, Brain, Target, BookOpen, 
  PenTool, Clock, MessageCircle, Crown, Lock 
} from 'lucide-react';
import { motion } from 'framer-motion';

const iconMap = {
  'zap': Zap,
  'flame': Flame,
  'brain': Brain,
  'target': Target,
  'trophy': Trophy,
  'book-open': BookOpen,
  'pen-tool': PenTool,
  'clock': Clock,
  'message-circle': MessageCircle,
  'crown': Crown,
};

export default function AchievementsPage() {
  const { getAuthHeader, API } = useAuth();
  const { t, i18n } = useTranslation();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    try {
      const response = await axios.get(`${API}/achievements`, { headers: getAuthHeader() });
      setAchievements(response.data);
    } catch (error) {
      console.error('Failed to fetch achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const unlockedCount = achievements.filter(a => a.unlocked).length;
  const totalCount = achievements.length;

  return (
    <Layout>
      <div className="space-y-8" data-testid="achievements-page">
        {/* Header */}
        <div>
          <h1 className="font-serif font-bold text-2xl md:text-3xl text-foreground">Achievements</h1>
          <p className="text-muted-foreground">Track your mental training milestones</p>
        </div>

        {/* Progress Overview */}
        <Card className="glass neon-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-6">
              <div className="p-4 rounded-full bg-primary/20">
                <Trophy className="h-10 w-10 text-primary" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="font-serif text-xl text-foreground">Your Progress</h2>
                  <span className="text-2xl font-bold text-primary">
                    {unlockedCount}/{totalCount}
                  </span>
                </div>
                <Progress value={(unlockedCount / totalCount) * 100} className="h-3" />
                <p className="text-sm text-muted-foreground mt-2">
                  {unlockedCount === totalCount 
                    ? "Congratulations! You've unlocked all achievements!" 
                    : `${totalCount - unlockedCount} achievements remaining`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Achievements Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="bg-card/50 border-white/5 animate-pulse">
                <CardContent className="p-6 h-40" />
              </Card>
            ))
          ) : (
            achievements.map((achievement, index) => {
              const Icon = iconMap[achievement.icon] || Trophy;
              const progressPercent = (achievement.progress / achievement.requirement) * 100;
              
              return (
                <motion.div
                  key={achievement.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className={`
                    transition-all duration-300 
                    ${achievement.unlocked 
                      ? 'bg-primary/10 border-primary/30 hover:border-primary/50' 
                      : 'bg-card/50 border-white/5 hover:border-white/10'}
                  `}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className={`
                          p-3 rounded-xl relative
                          ${achievement.unlocked 
                            ? 'bg-primary/20' 
                            : 'bg-muted/50'}
                        `}>
                          <Icon className={`h-6 w-6 ${achievement.unlocked ? 'text-primary' : 'text-muted-foreground'}`} />
                          {!achievement.unlocked && (
                            <Lock className="h-3 w-3 text-muted-foreground absolute -bottom-1 -right-1" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-serif font-semibold text-lg ${achievement.unlocked ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {achievement.title}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-3">
                            {achievement.description}
                          </p>
                          
                          {achievement.unlocked ? (
                            <div className="flex items-center gap-2 text-sm text-secondary">
                              <Trophy className="h-4 w-4" />
                              <span>Unlocked!</span>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Progress</span>
                                <span className="text-foreground">{achievement.progress}/{achievement.requirement}</span>
                              </div>
                              <Progress value={progressPercent} className="h-1.5" />
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })
          )}
        </div>

        {/* Achievement Tips */}
        <Card className="bg-card/30 border-white/5">
          <CardContent className="p-6">
            <h3 className="font-serif text-lg text-foreground mb-3">Tips to Unlock More</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-primary" />
                Complete daily meditation sessions to build your streak
              </li>
              <li className="flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-accent" />
                Write journal entries to reflect on your progress
              </li>
              <li className="flex items-center gap-2">
                <Target className="h-4 w-4 text-secondary" />
                Set and complete goals to track your achievements
              </li>
              <li className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-chart-5" />
                Chat with your AI Coach for personalized guidance
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
